import { PixelCrop } from 'react-image-crop';

// Crop an image using canvas, given the pixel crop area from react-image-crop
// Scales from display coordinates to natural image dimensions for full-resolution output
export const getCroppedImage = (image: HTMLImageElement, cropArea: PixelCrop): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = Math.round(cropArea.width * scaleX);
    canvas.height = Math.round(cropArea.height * scaleY);

    ctx.drawImage(
      image,
      Math.round(cropArea.x * scaleX),
      Math.round(cropArea.y * scaleY),
      canvas.width,
      canvas.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to crop image'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.95
    );
  });
};

// Function to compress an image blob to a specific max size (in bytes)
// Returns a new blob that is compressed to be under the max size
export const compressImage = async (imageBlob: Blob, maxSizeInBytes: number = 2 * 1024 * 1024): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(imageBlob);

    img.onload = () => {
      // Release the object URL
      URL.revokeObjectURL(img.src);

      let quality = 0.9; // Start with high quality
      let canvas = document.createElement('canvas');
      let ctx = canvas.getContext('2d');

      // Calculate dimensions while maintaining aspect ratio
      let maxWidth = 1200;
      let maxHeight = 1200;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height = Math.round(height * maxWidth / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round(width * maxHeight / height);
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
      }

      // Function to reduce quality until we get under max size
      const tryCompression = (currentQuality: number) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          // If we're at the minimum quality or under max size, return the blob
          if (blob.size <= maxSizeInBytes || currentQuality <= 0.3) {
            resolve(blob);
          } else {
            // Otherwise try with a lower quality
            const newQuality = Math.max(currentQuality - 0.1, 0.3);
            tryCompression(newQuality);
          }
        }, 'image/jpeg', currentQuality);
      };

      // Start compression
      tryCompression(quality);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image for compression'));
    };
  });
};